import { Box, Stack, SxProps, Tab, Tabs, TabsProps, Theme } from "@mui/material";
import { createContext, PropsWithChildren, ReactNode, useContext } from "react";
import { TabProps } from "@mui/material/Tab/Tab";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const SenecTabContext = createContext<any>(undefined);
export const useSenecTabContext = () => useContext(SenecTabContext);
export const SenecTabContextProvider = <T,>({ value, children }: PropsWithChildren<{ value: T }>) => {
    return <SenecTabContext.Provider value={value}>{children}</SenecTabContext.Provider>;
};

export interface SenecTabConfig<T> extends Omit<TabProps, "label" | "value"> {
    label: string;
    value: T;
    "data-testid"?: string;
}

export interface SenecTabListProps<T> extends TabsProps {
    tabsConfig: SenecTabConfig<T>[];
    /**
     * When set true all Tabs will stretch over parent width
     */
    fullWidth?: boolean;
}

export const SenecTabList = <T,>({ tabsConfig, fullWidth, ...tabsProps }: SenecTabListProps<T>) => {
    const currentValue = useContext<T>(SenecTabContext);

    return (
        <Stack position={"relative"}>
            <Tabs aria-label="tabmenu" value={currentValue} {...tabsProps}>
                {tabsConfig.map((config) => (
                    <Tab
                        {...config}
                        key={config.label}
                        value={config.value}
                        label={config.label}
                        data-testid={config["data-testid"]}
                        sx={{
                            flexGrow: fullWidth ? 1 : "unset",
                            ...(fullWidth ? { maxWidth: "100%" } : {}),
                        }}
                    />
                ))}
            </Tabs>
        </Stack>
    );
};

export interface SenecTabPanelProps<T> {
    /**
     * value associated with the currently selected tab
     */
    value: T;
    /**
     * MUI styling properties
     */
    sx?: SxProps<Theme>;
    children?: ReactNode;
}

export const SenecTabPanel = <T,>({ value, sx, children }: SenecTabPanelProps<T>) => {
    const currentValue = useContext<T>(SenecTabContext);

    return (
        <Box role="tabpanel" hidden={currentValue !== value} sx={{ marginTop: "1rem", ...sx }}>
            {currentValue === value && <>{children}</>}
        </Box>
    );
};
